// src/components/avisEnseignants/apiAvisEnseignants.js
// Appels de la vie scolaire vers /api/avis-enseignants (jeton employé).
//
// C'est le jeton de l'ADMINISTRATION qui part ici (getAuthHeaders, clé
// `token`) : ces routes sont celles de l'école qui lit les avis, pas celles de
// l'enseignant qui les donne (src/espace-enseignant/api.js, jeton enseignant,
// jamais lu par ce fichier).
//
// ⚠️ Un avis d'enseignant n'est JAMAIS montré à la famille : aucune donnée lue
// ici ne doit être recopiée vers un écran ou une route de l'espace famille.
// Pour qu'un fait remonte aux parents, la vie scolaire crée elle-même un
// incident qu'elle rédige (`creerIncident`).

import { getAuthHeaders } from '../../services/api';

const RACINE = '/api/avis-enseignants';

const LICENCE_FERMEE = "L'espace enseignant n'est pas ouvert pour cet établissement.";
const NON_INSTALLE = "Les avis des enseignants ne sont pas encore installés dans cet établissement.";

// Le refus de licence (`requireFeature`) ne porte pas de `message` mais un
// `error` au libellé technique : on le reconnaît à son code pour dire la même
// chose que le reste de l'écran. Une route absente du serveur (404 sans corps
// JSON, ou 503 d'une table pas encore créée) dit que le module n'est pas
// installé. Les autres refus ont leur `message` en français, affiché tel quel.
function erreurDuServeur(reponse, donnees, defaut) {
  let message = donnees?.message;
  if (!message && (donnees?.code === 'FEATURE_NOT_LICENSED' || reponse.status === 403)) message = LICENCE_FERMEE;
  if (!message && (reponse.status === 503 || (reponse.status === 404 && !donnees))) message = NON_INSTALLE;
  const erreur = new Error(message || donnees?.error || defaut);
  erreur.code = donnees?.code;
  erreur.statut = reponse.status;
  return erreur;
}

async function appeler(chemin, { methode = 'GET', corps } = {}) {
  let reponse;
  try {
    reponse = await fetch(`${RACINE}${chemin}`, {
      method: methode,
      headers: getAuthHeaders(),
      body: corps !== undefined ? JSON.stringify(corps) : undefined
    });
  } catch {
    throw new Error('Connexion au serveur impossible.');
  }
  let donnees = null;
  try { donnees = await reponse.json(); } catch { donnees = null; }
  if (!reponse.ok) throw erreurDuServeur(reponse, donnees, 'Une erreur est survenue.');
  return donnees;
}

/** Les filtres vides (« tous », « toutes les classes ») ne partent pas dans l'adresse. */
const requete = (filtres = {}) => {
  const params = new URLSearchParams(
    Object.entries(filtres).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );
  const texte = params.toString();
  return texte ? `?${texte}` : '';
};

export const avisEnseignants = {
  /** `{ statut, tonalite, classe_id, q, page }` → `{ droits, compteurs, total, page, par_page, avis }`. */
  liste: (filtres) => appeler(requete(filtres)),
  marquerLu: (avisId) => appeler(`/${avisId}/lu`, { methode: 'POST' }),
  /** `{ type, titre, description, gravite, date_evenement? }` — tout est rédigé par la vie scolaire. */
  creerIncident: (avisId, incident) => appeler(`/${avisId}/incident`, { methode: 'POST', corps: incident })
};
